import cookie from "js-cookie";
import Head from "next/head";
import { useRouter } from "next/router";
import React, { useEffect, useState } from "react";
import { Breadcrumb } from "react-bootstrap";
import { getStatesCities, GetStoreData } from "../../../api.services.js/others";
import {
  NonObtbList,
  ObtbList,
  WtspModel,
} from "../../../src/components/store-locator";
import Loader from "../../../src/components/loader";
import AddressAutocomplete from "../../../src/components/storeSuggestion";

/**
 * Server-side props for the /store-locator/[state] page.
 *
 * This function fetches store data based on the state or city parameter
 * passed in the URL. It first checks if the state is in the list of states
 * fetched from the CMS, and if so, it fetches the store data using the
 * GetStoreData API with the "advanced" search type. If the state is not in
 * the list, it attempts to geocode the city using the Google Maps API and
 * fetches the store data using the GetStoreData API with the latitude and
 * longitude.
 *
 * If the store data is successfully fetched, it returns an object with the
 * following props:
 * - statesData: An array of state objects fetched from the CMS
 * - storeData: An array of store objects fetched from the GetStoreData API
 * - error: null
 * - state: The state parameter passed in the URL
 * - searchType: The search type used to fetch the store data ("advanced" or "simple")
 *
 * If an error occurs while fetching the store data, it returns an object with
 * the following props:
 * - statesData: An empty array
 * - storeData: An empty array
 * - error: The error message
 * - state: The state parameter passed in the URL
 * - searchType: The search type used to fetch the store data ("advanced" or "simple")
 *
 * @param {Object} context The context object passed by Next.js
 * @returns {Object} An object with the props to be passed to the page component
 */
export const getServerSideProps = async (context) => {
  try {
    let { state } = context.params;
    let longitude, latitude;
    // Format the state string
    state = state
      .replace(/-/g, " ") // Replace hyphens with spaces
      .toLowerCase() // Convert entire string to lowercase
      .replace(/\b\w/g, (char) => char.toUpperCase()); // Capitalize first letter of each word

    // Fetch states data
    const response = await getStatesCities({});
    const statesData = response?.data?.getStates || [];
    let storeData = [],
      searchType = "";

    // Determine search type based on state presence in statesData
    searchType =
      statesData.length > 0 && statesData.find((obj) => obj.state === state)
        ? "advanced"
        : "simple";

    let apiResponse;

    if (searchType === "advanced") {
      // Fetch store data using advanced search
      apiResponse = await GetStoreData({
        filter: { state, city: "", searchType },
      });
    } else {
      // Use Google Maps Geocoding API on the server
      const googleMapsApiKey = "AIzaSyBrmlHMs5JUON_ErXHEKk_HSd2UoLN20HQ"; // Store API key in .env
      const geocodeUrl = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
        state
      )}&region=in&key=${googleMapsApiKey}`;

      // Response from the Google Maps Geocoding API of city
      const geocodeResponse = await fetch(geocodeUrl);
      const geocodeData = await geocodeResponse.json();

      if (geocodeData.status === "OK" && geocodeData.results[0]) {
        const location = geocodeData.results[0].geometry.location;
        latitude = location.lat;
        longitude = location.lng;

        // Fetch store data using geocoded latitude and longitude
        apiResponse = await GetStoreData({
          filter: {
            latitude,
            longitude,
            searchType,
          },
        });
      } else {
        throw new Error("Error fetching geocode data: " + geocodeData.status);
      }
    }

    // Check if store data was fetched successfully
    if (apiResponse?.data?.getStoreData) {
      storeData = apiResponse?.data?.getStoreData;
    } else {
      throw new Error("Failed to fetch store data");
    }
    return {
      props: {
        statesData,
        storeData,
        cityNotFound: storeData.city_not_found,
        state,
        isState: searchType === "advanced" ? true : false,
        error: null,
      },
    };
  } catch (error) {
    console.error("Error fetching data:", error); // Log error for debugging

    return {
      props: {
        statesData: [],
        storeData: [],
        state: "",
        error: error.message || "Error fetching data",
      },
    };
  }
};

function StateFromAdvanceOrCityFromSimpleSearch({
  statesData,
  storeData,
  cityNotFound,
  state,
  isState,
  error,
}) {
  let router = useRouter();

  const [activeTabTop, setActiveTabTop] = useState("advanced-search");
  const [activeTab, setActiveTab] = useState("all");
  const [cityFromSimpleSearch, setCityFromSimpleSearch] = useState("");
  const [cityFromAdvancedSearch, setCityFromAdvancedSearch] = useState("");
  const [stateFromAdvancedSearch, setStateFromAdvancedSearch] = useState("");
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [cityOptions, setCityOptions] = useState([]);
  const [selectedCity, setSelectedCity] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (error) {
      router.push("/404");
    }
  }, [error, router]); // Redirect when error changes

  if (error) return null;

  const coco =
    storeData?.obtbs?.filter(
      (store) => store.alternative_name === "Company Owned Store"
    ) || [];

  useEffect(() => {
    if (coco.length === 0 && activeTab === "Signature-Showrooms") {
      setActiveTab("all");
    }
  }, [coco]);

  // Function to send wtsp to the customer

  // selecting
  useEffect(() => {
    setStateFromAdvancedSearch(state);
    setCityOptions(
      statesData.find((item) => item.state === state)?.cities || []
    );
  }, []);

  const handleCityChange = (event) => {
    if (event.target.value) {
      let selectedCityValue = event.target.value;
      setSelectedCity(selectedCityValue);
      setCityFromAdvancedSearch(selectedCityValue);
    }
  };

  const handleStateChange = (event) => {
    const selectedStateValue = event.target.value;
    setStateFromAdvancedSearch(selectedStateValue);
    setCityFromAdvancedSearch("");
    setSelectedCity(""); // Reset city selection
    setCityOptions(
      statesData.find((item) => item.state === selectedStateValue)?.cities || []
    );
  };

  const handleStoreDetailClicked = async (store) => {
    let tem = store.alternative_name;
    let endpoint = tem.toLowerCase().split(" ").join("-");
    cookie.remove("cp");
    cookie.set("placeId", store.gmaps_place_id, { expires: 1, path: "/" }); // Expires in 1 day
    // console.log("yfytfyt ", store);
    router.push(`/store-locator/tiles-dealer/${endpoint}`);
  };
  const handleNonObtbClick = (store) => {
    cookie.remove("placeId");
    cookie.set("cp", store.no);
    // console.log("333 ", store);
    let tem = store.name;
    let endpoint = tem.toLowerCase().split(" ").join("-");

    router.push(`/store-locator/tiles-dealer/${endpoint}`);
  };

  const handleAdvancedSearch = async (state, city) => {
    setLoading(true);

    // Format state and city to URL-friendly format
    let formattedState = state ? state.toLowerCase().replace(/\s+/g, "-") : "";
    let formattedCity = city ? city.toLowerCase().replace(/\s+/g, "-") : "";

    // Navigate to the appropriate URL based on the presence of state or city
    if (city) {
      router.push(`/store-locator/${formattedState}/${formattedCity}`);
    } else if (state) {
      router.push(`/store-locator/${formattedState}`);
    }

    setLoading(false);
  };

  if (loading) return <Loader />;

  // add redux variable which manages query params or extract from url
  return (
    <>
      <Head>
        <meta
          name="description"
          content={
            !isState
              ? `Explore Orientbell tile collections in ${state}. Wide range of designs for walls and bathrooms in ${state}. Locate your nearest tiles store now!`
              : `Find the nearest Orientbell Tiles dealer in ${state}. Discover premium floor tiles and a wide range of designs for walls and bathrooms. Visit our store for expert advice and great deals!`
          }
        />
        <title>
          Orientbell Tiles Dealers in {state} | Find a Store Near You
        </title>
        <meta
          name="title"
          content={`Orientbell Tiles Dealers in ${state} | Find a Store Near You`}
        />
      </Head>
      <div className="obl-store-page">
        <div className="obl-store-intro-section">
          <div className="container">
            <Breadcrumb>
              <Breadcrumb.Item href="/">Home</Breadcrumb.Item>
              <Breadcrumb.Item href="/store-locator">
                Store Locator
              </Breadcrumb.Item>
              <Breadcrumb.Item
                href={`/store-locator/${state
                  .toLowerCase()
                  .replace(/\s+/g, "-")}`}
              >
                {state}
              </Breadcrumb.Item>
            </Breadcrumb>

            <div className="obl-store-intro-row">
              <div className="obl-store-intro-left">
                <h1>Orientbell Tiles Dealers</h1>
                <div className="store-search-section-inner">
                  <p>Locate The Nearest Tiles Stores</p>
                  <div className="obl-store-tab-section">
                    <div className="storetabs-menu">
                      <a
                        className={
                          activeTabTop === "simple-search"
                            ? "tab-item active"
                            : "tab-item"
                        }
                        onClick={() => {
                          setActiveTabTop("simple-search");
                          localStorage.removeItem("cityFromAdvancedSearch");
                          localStorage.removeItem("stateFromAdvancedSearch");
                          setCityFromAdvancedSearch("");
                          setStateFromAdvancedSearch("");
                        }}
                      >
                        {" "}
                        Search City{" "}
                      </a>
                      <a
                        className={
                          activeTabTop === "advanced-search"
                            ? "tab-item active"
                            : "tab-item"
                        }
                        onClick={() => {
                          setActiveTabTop("advanced-search");
                          localStorage.removeItem("cityFromSimpleSearch");
                          localStorage.removeItem("latitude");
                          localStorage.removeItem("longitude");
                          setCityFromSimpleSearch("");
                        }}
                      >
                        Advanced Search
                      </a>
                    </div>
                    <div className="obl-store-tab-data">
                      <div
                        className={
                          activeTabTop === "simple-search"
                            ? "tab-data-col search-nearme active"
                            : "tab-data-col search-nearme "
                        }
                      >
                        <AddressAutocomplete />
                      </div>
                      <div
                        className={
                          activeTabTop === "advanced-search"
                            ? "tab-data-col search-location active"
                            : "tab-data-col search-location"
                        }
                      >
                        <fronm>
                          <ul className="list-unstyled search-location">
                            <li className="input-group">
                              <label for="OutletState">
                                State<sup>*</sup>
                              </label>

                              <select
                                name="state"
                                className="input"
                                required="required"
                                id="OutletState"
                                value={stateFromAdvancedSearch}
                                onChange={handleStateChange}
                              >
                                <option value="">Select a state</option>
                                {statesData.map((state) => (
                                  <option key={state.state} value={state.state}>
                                    {state.state}
                                  </option>
                                ))}
                              </select>
                            </li>
                            <li className="input-group">
                              <label for="OutletCity">City</label>

                              <select
                                name="city"
                                className="input"
                                title="Please select state first"
                                id="OutletCity"
                                disabled={!stateFromAdvancedSearch}
                                value={cityFromAdvancedSearch}
                                onChange={handleCityChange}
                              >
                                <option value="">Select a city</option>
                                {cityOptions.map((city) => (
                                  <option key={city} value={city}>
                                    {city}
                                  </option>
                                ))}
                              </select>
                            </li>

                            <li className="actions">
                              <div className="obl-store-actions-btn">
                                <button
                                  onClick={() =>
                                    handleAdvancedSearch(
                                      stateFromAdvancedSearch,
                                      cityFromAdvancedSearch
                                    )
                                  }
                                >
                                  <img
                                    src="/search-mobile-icon1.svg"
                                    alt="Search"
                                  />{" "}
                                  Search
                                </button>
                              </div>
                            </li>
                          </ul>
                        </fronm>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="obl-store-intro-right">
                <a href="#">
                  <img src="/OBL-Sun-Proof-Your-Home-1200x400.avif" />
                </a>
              </div>
            </div>

            {cityNotFound ? (
              <p className="simple-txt-effect-store-loc">
                We could not find any stores in your area. Here are few stores
                you might want to check out or Simply try our
                <a
                  onClick={() => setActiveTabTop("advanced-search")}
                  style={{ cursor: "pointer" }}
                  // TODO: add color change on hover
                >
                  {" "}
                  <strong>Advanced search</strong>
                </a>
              </p>
            ) : (
              <>
                {Array.isArray(storeData.obtbs) &&
                (storeData.obtbs.length > 0 || storeData.nonObtbs.length > 0) &&
                Array.isArray(storeData.nonObtbs) ? (
                  <h2>Orientbell Tiles Dealers in {state}</h2>
                ) : (
                  <h2>No Stores in {state}</h2>
                )}
              </>
            )}

            {Array.isArray(storeData.obtbs) &&
            Array.isArray(storeData.nonObtbs) &&
            (storeData.obtbs.length > 0 || storeData.nonObtbs.length > 0) ? (
              <div className="store_locator_tab_section">
                <ul
                  className={
                    coco.length > 0
                      ? "tab_head tab-four-columns"
                      : "tab_head tab-three-columns"
                  }
                >
                  <li
                    className={activeTab == "all" && "active"}
                    onClick={() => setActiveTab("all")}
                  >
                    All
                  </li>
                  <li
                    className={activeTab == "OBL-Boutiques" && "active"}
                    onClick={() => {
                      setActiveTab("OBL-Boutiques");
                    }}
                  >
                    Top Stores
                  </li>
                  <li
                    className={activeTab == "Retail-Stores" && "active"}
                    onClick={() => setActiveTab("Retail-Stores")}
                  >
                    Stores In Your Area
                  </li>
                  {coco.length > 0 && (
                    <li
                      className={activeTab == "Signature-Showrooms" && "active"}
                      onClick={() => setActiveTab("Signature-Showrooms")}
                    >
                      {" "}
                      Company Showrooms
                    </li>
                  )}{" "}
                </ul>
                <div className="tab_head_select">
                  <select
                    onChange={(e) => {
                      setActiveTab(e.target.value);
                    }}
                  >
                    <option value="all">All</option>
                    <option value="OBL-Boutiques">Top Stores</option>
                    <option value="Retail-Stores"> Stores In Your Area</option>
                    <option value="Signature-Showrooms">
                      {" "}
                      Company Showrooms
                    </option>
                  </select>
                </div>
                <div className="store_locator_tab_data">
                  <div className="store_locator_row">
                    {activeTab === "all" ? (
                      // list of stores obtb then non obtb
                      <>
                        <ObtbList
                          obtb={storeData.obtbs}
                          setModalIsOpen={(value) => setModalIsOpen(value)}
                          all
                        />
                        <NonObtbList
                          nonObtb={storeData.nonObtbs}
                          setModalIsOpen={(value) => setModalIsOpen(value)}
                          all
                        />
                      </>
                    ) : activeTab == "OBL-Boutiques" ? (
                      <>
                        {" "}
                        {Array.isArray(storeData.obtbs) &&
                        storeData.obtbs.length > 0 ? (
                          <ObtbList
                            obtb={storeData.obtbs}
                            setModalIsOpen={(value) => setModalIsOpen(value)}
                          />
                        ) : (
                          <div className="no-store-row">
                            <h2>No Top Stores Found In {state} </h2>
                          </div>
                        )}
                      </>
                    ) : activeTab == "Retail-Stores" ? (
                      <NonObtbList
                        nonObtb={storeData.nonObtbs}
                        setModalIsOpen={(value) => setModalIsOpen(value)}
                      />
                    ) : activeTab == "Signature-Showrooms" ? (
                      // coco stores
                      <ObtbList
                        obtb={coco}
                        setModalIsOpen={(value) => setModalIsOpen(value)}
                      />
                    ) : (
                      <></>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="no-store-row">
                <h2>
                  No Stores Found In {state} <br />
                  <a
                    onClick={() => setActiveTabTop("advanced-search")}
                    style={{ cursor: "pointer" }}
                    // TODO: add color change on hover
                  >
                    <h2>Try Advanced Search</h2>
                  </a>
                </h2>
              </div>
            )}
            <div className="design-pg-seo-content">
              {!isState ? (
                <h2>
                  Find Premium Tiles at Your Nearest Orientbell Tiles Store in{" "}
                  {state}
                </h2>
              ) : (
                <h2>
                  Find Premium Tiles At Nearest Orientbell Tiles Store in{" "}
                  {state}
                </h2>
              )}
              {state ? (
                <div>
                  <p>
                    Whether you are planning to restyle your home interiors or
                    design your commercial space, you must be looking for
                    stylish and trending tile designs that can turn your space
                    into a trendsetter. From wooden charms to minimalist looks
                    to stone textures, you can find multiple new designs for
                    every space decor and style. However, the question remains -
                    where to find the best wall or floor tile in {state}? The
                    easiest way to find the best tile store is by exploring the
                    nearby tiles showroom in your city that has a reputation for
                    offering quality tiles. A good tile store usually has a good
                    display of the latest tiles, offering a satisfying
                    tile-buying experience and allowing you to find the right
                    tiles for your space.
                  </p>

                  <p>
                    Whether you want to upgrade your home with new tiles or want
                    to make your commercial space such as offices, libraries,
                    hospitals, hotels, and more stand out with pristine and
                    charismatic tiles, visiting the best tile shops in {state}{" "}
                    is a must. Not only can professional dealers offer you
                    excellent deals and high-quality products, but they can also
                    offer you expert opinions, suggestions, and advice. Visit
                    the Orientbell Tiles shop in {state} to experience a new way
                    to find and buy tiles. Right from different patterns,
                    styles, colours, and more, thanks to Orientbell Tiles, you
                    will always find your perfect tile design in {state}.
                  </p>

                  <p>
                    Finding the most perfect tile showroom in {state} may seem
                    to be a difficult task for many, especially if you are not
                    aware of the tile market. For those who would like to know
                    more about tiles and how to find the best one, consulting
                    with a nearby tiles showroom can be a great choice. So why
                    wait? Visit your neighbouring Orientbell Tiles showroom
                    today to find an exciting range of products designed just
                    for you.
                  </p>
                </div>
              ) : (
                <div>
                  <p>
                    While building your dream house or renovating your
                    commercial space, an important aspect that you have to focus
                    on is the materials used, and tiles are an important element
                    among them. Tiles come in hundreds of unique designs, from
                    classic to modern. Not only are they highly versatile but
                    also easy to maintain, lasting for years with easy upkeep.
                    Be it your living room wall, parking space flooring, or
                    kitchen accent wall, there is an almost limitless choice for
                    every style and budget, provided you reach out to a good
                    tile store in your city. Finding the best tile store near
                    you is easy as you can simply google “best tile shop near{" "}
                    {state}” or “best tile shop in {state}”.
                  </p>

                  <p>
                    Depending on your requirements and needs, you can find a
                    plethora of tile stores in different tile markets in {state}
                    . Though it's tempting to rely just on a tile shop's fame,
                    it's important to remember that this doesn't always
                    guarantee the best quality products. This is why it is best
                    to choose only the reliable and trustworthy tile showroom so
                    that you don't have to compromise on quality.
                  </p>

                  <p>
                    So, if quality, affordability, and variety are the three
                    things that you are looking for while buying tiles, then
                    look no further and visit your nearest Orientbell Tiles
                    Boutique today. We offer the best tiles at competitive
                    prices in {state}, making your tile shopping experience
                    hassle-free and rewarding.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <WtspModel
        modalIsOpen={modalIsOpen}
        setModalIsOpen={(value) => setModalIsOpen(value)}
      />
    </>
  );
}

export default StateFromAdvanceOrCityFromSimpleSearch;
